import { useEffect, useState } from "react";

// Chrome/Edge fire `beforeinstallprompt` when the PWA is installable.  We
// stash that event and surface a small "Install" button next to the header
// chrome.  Once the user installs (or dismisses + the browser stops firing
// the event), the button stays hidden until the next eligible visit.
//
// Safari / iOS don't fire this event — users add to home screen from the
// share sheet there, and we don't currently show any prompt for that
// because surfacing iOS-specific instructions inline is more noise than
// help.

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

export function InstallButton() {
  const [prompt, setPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    // Already running as a standalone app — nothing to install.
    if (window.matchMedia?.("(display-mode: standalone)").matches) {
      setInstalled(true);
      return;
    }

    const onPrompt = (e: Event) => {
      e.preventDefault();
      setPrompt(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed || !prompt) return null;

  const install = async () => {
    try {
      await prompt.prompt();
      const choice = await prompt.userChoice;
      if (choice.outcome === "accepted") setInstalled(true);
    } finally {
      // The same event can only be used once.
      setPrompt(null);
    }
  };

  return (
    <button
      onClick={() => void install()}
      className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
      title="Install Seneca as an app on this device"
    >
      Install
    </button>
  );
}
